import { ClipboardIcon } from "@heroicons/react/outline";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection";
import { Tag } from "components/tag";
import { IOrderDetail } from "interfaces";
import moment from "moment";
import toast from "react-hot-toast";
import {
  convertCurrency,
  currencySymbol,
  formatNumber,
  getOrderStatusLabel,
  getOrderTypeLabel,
  getPaymentMethodLabel,
  getTicketPlatformLabel,
} from "utills/helpers";

export const OrderSection = ({
  orderDetailData,
  rate,
}: {
  orderDetailData: IOrderDetail | null;
  rate: number;
}) => {
  const copyToClipboard = async (text: string) => {
    if (!text) return false;

    try {
      await navigator.clipboard.writeText(text);

      toast.success("Copied to clipboard");

      return true;
    } catch (err) {
      console.error("Copy failed:", err);

      toast.error("Copied to clipboard failed");

      return false;
    }
  };

  return (
    <CollapsibleSection label="Order Information" defaultOpen={true}>
      <div
        className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6"
        style={{ wordBreak: "break-word" }}
      >
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">Order status</p>
          </div>
          <div className="col-span-4">
            <Tag
              text={getOrderStatusLabel(orderDetailData?.order_status ?? "")}
              type={orderDetailData?.order_status}
            />
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">Order price</p>
          </div>
          <div className="col-span-4">
            <p className="text-brand-secondary-500 font-semibold">
              {currencySymbol()}{" "}
              {convertCurrency(
                orderDetailData?.grand_total ?? 0,
                orderDetailData?.currency ?? "",
                rate
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">Total discount amount</p>
          </div>
          <div className="col-span-4">
            <p>
              {currencySymbol()}{" "}
              {convertCurrency(
                orderDetailData?.discount_coupon ?? 0,
                orderDetailData?.currency ?? "",
                rate
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <p className="font-medium">Payment unit</p>
          </div>
          <div className="col-span-4">
            <p className="uppercase">{orderDetailData?.currency}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
          <div className="col-span-2">
            <p className="font-medium">Payment method</p>
          </div>
          <div className="col-span-4">
            <p>
              {getPaymentMethodLabel(
                orderDetailData?.transaction?.parent_payment_method ?? ""
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-11">
          <div className="col-span-2">
            <p className="font-medium">Transaction code</p>
          </div>
          <div className="col-span-4 flex flex-wrap items-center gap-3">
            <p>{orderDetailData?.transaction?.reference_number}</p>

            <Button
              className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
              onClick={() => {
                copyToClipboard(orderDetailData?.transaction?.reference_number ?? "");
              }}
            >
              <ClipboardIcon className="w-4 h-4 text-black" />
            </Button>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[13]">
          <div className="col-span-2">
            <p className="font-medium">Event name</p>
          </div>
          <div className="col-span-4">
            <p>{orderDetailData?.event_name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[15]">
          <div className="col-span-2">
            <p className="font-medium">Total discount promotion</p>
          </div>
          <div className="col-span-4">
            <p>
              {currencySymbol()}{" "}
              {convertCurrency(
                orderDetailData?.discount_promotion ?? 0,
                orderDetailData?.currency ?? "",
                rate
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[17]">
          <div className="col-span-2">
            <p className="font-medium">Order type</p>
          </div>
          <div className="col-span-4">
            <p>{getOrderTypeLabel(orderDetailData?.order_type ?? "")}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[19]">
          <div className="col-span-2">
            <p className="font-medium">Ticket platform</p>
          </div>
          <div className="col-span-4">
            <p>
              {getTicketPlatformLabel(orderDetailData?.ticket_platform ?? "")}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">Created at</p>
          </div>
          <div className="col-span-4">
            <p>
              {orderDetailData?.created_at
                ? moment(orderDetailData.created_at).format(
                    "DD/MM/YYYY HH:mm:ss"
                  )
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">Payment at</p>
          </div>
          <div className="col-span-4">
            <p>
              {orderDetailData?.purchased_at
                ? moment(orderDetailData.purchased_at).format(
                    "DD/MM/YYYY HH:mm:ss"
                  )
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <p className="font-medium">Total items</p>
          </div>
          <div className="col-span-4">
            <p>{formatNumber(orderDetailData?.total_item ?? 0)}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">Ticket name</p>
          </div>
          <div className="col-span-4">
            <p>{orderDetailData?.item_description_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
          <div className="col-span-2">
            <p className="font-medium">E-ticket usage date</p>
          </div>
          <div className="col-span-4">
            <p>
              {orderDetailData?.ticket_use_for_date
                ? moment(orderDetailData.ticket_use_for_date).format(
                    "DD/MM/YYYY"
                  )
                : ""}
              {orderDetailData?.showtime
                ? ` - ${orderDetailData.showtime}`
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-12">
          <div className="col-span-2">
            <p className="font-medium">Group name</p>
          </div>
          <div className="col-span-4">
            <p>{orderDetailData?.group_name}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[14]">
          <div className="col-span-2">
            <p className="font-medium">Org name</p>
          </div>
          <div className="col-span-4">
            <p>{orderDetailData?.organization?.name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[16]">
          <div className="col-span-2">
            <p className="font-medium">Total participants</p>
          </div>
          <div className="col-span-4">
            <p>{formatNumber(orderDetailData?.total_attendant ?? 0)}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[18]">
          <div className="col-span-2">
            <p className="font-medium">Partner order code</p>
          </div>
          <div className="col-span-4">
            {/* <p>
              
            </p> */}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[20]">
          <div className="col-span-2">
            <p className="font-medium">Private flow</p>
          </div>
          <div className="col-span-4">
            <p>{orderDetailData?.auth_identifier}</p>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
